// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { format } from "date-fns";
import actionCreators from "@modules/shared/actionCreator";
import { GSGrid, Pagination, View } from "@components";
import type { StepProps } from "@components";
import type { PaginationExtendedType } from "@types";
import type { EmployeeType } from "@modules/employee/types";
import type {
  EmployeeRefType,
  NotificationActionType,
} from "@modules/shared/reducer/employee-notifications/types";
import {
  getAddNotificationModalList,
  selectAddNotificationModalPagination,
} from "@modules/shared/selectors/employee-notifications";
import { getCurrentPath } from "@selectors";
import Step1SelectEmployeeFilter from "./step1-select-employee-filter";

type OwnProps = $Exact<{
  data: ?EmployeeType,
  clientId: number,
  employeeRefType: EmployeeRefType,
  actionType?: NotificationActionType,
  isLoading?: boolean,
}>;

type PropsFromState = $Exact<{
  employees: Array<EmployeeType>,
  pagination: PaginationExtendedType,
  currentPath: string,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeeList: typeof actionCreators.GetAddNotificationModalEmployeeList,
  resetEmployeeList: typeof actionCreators.ResetAddNotificationModalEmployeeList,
}>;

type Props = $Exact<{
  ...StepProps,
  ...PropsFromLocalize,
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;
type State = $Exact<{
  filters: any,
  page: any,
}>;
export class SelectEmployee extends React.Component<Props, State> {
  constructor() {
    super();
    this.state = {
      filters: null,
      page: null,
      searchSubmitted: false,
    };
  }

  handleSubmit = (employee: EmployeeType) => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(employee, true);
    }
  };

  handlePaginationChange = (page: number, filters: *) => {
    this.setState({
      page,
    });
    this.props.getEmployeeList({
      clientId: this.props.clientId,
      page,
      filters: {
        ...this.state.filters,
        dateOfBirth: this.dateOfBirth,
      },
    });
  };
  handleResetFilter = () => {
    this.props.resetEmployeeList();
    this.setState({
      page: null,
      filters: null,
      searchSubmitted: false,
    });
  };

  handleFilterChange = (filters: *) => {
    this.setState({
      page: null,
      filters: {
        ...filters,
        isActive: true,
      },
    });
  };

  handleFilterSubmit = (filters: *) => {
    this.setState({
      page: null,
      searchSubmitted: true,
    });

    this.props.getEmployeeList({
      clientId: this.props.clientId,
      filters: {
        ...this.state.filters,
        dateOfBirth: this.dateOfBirth,
      },
      page: this.state.page,
    });
  };

  get dateOfBirth() {
    const date = this.state.filters && this.state.filters.dateOfBirth;
    return date ? format(date, "YYYY-MM-DD") : null;
  }

  get columns() {
    const columnsConfig = [
      {
        key: "id",
        align: "left",
        name: this.props.t("ID"),
        hidden: !!this.props.actionType,
      },
      {
        key: "employeeRefPrimary",
        align: "left",
        name: this.props.t("EMPLOYEE_REF"),
      },
      {
        name: this.props.t("FIRST_NAME"),
        key: "firstName",
        width: 100,
        align: "left",
      },
      {
        name: this.props.t("SURNAME"),
        key: "lastName",
        width: 100,
        align: "left",
      },
      {
        name: this.props.t("DEPARTMENT"),
        key: "department",
        renderCell: ({ row }: { row: EmployeeType }) => {
          if (row.employmentSummary.organisationalUnits.length) {
            return GSGrid.render.ellipsis(row.employmentSummary.organisationalUnits.join(", "));
          }
          return this.props.t("N/A");
        },
        align: "left",
        width: 460,
      },
      {
        name: this.props.t("PRIMARY_EMAIL_ADDRESS"),
        key: "emailAddress",
        width: 250,
        renderCell: ({ row }: { row: EmployeeType }) => {
          if ((row.contactDetails || {}).workEmail) {
            return (row.contactDetails || {}).workEmail;
          }

          return <span className="text-negative">{this.props.t("MISSING")}</span>;
        },
        align: "left",
      },
    ];

    if (this.props.editable) {
      columnsConfig.push({
        name: this.props.actionType ? this.props.t(this.props.actionType.toUpperCase()) : "",
        key: "actions",
        frozen: "right",
        width: this.props.actionType ? 70 : 50,
        onCellClick: (row: Object) => this.handleSubmit(row),
        renderCell: () => GSGrid.render.materialIcon("play_circle"),
      });
    }

    return columnsConfig;
  }

  render() {
    if (!this.props.editable) {
      return (
        <div className="flex-fill">
          {this.props.data && <GSGrid rows={[this.props.data]} columns={this.columns} />}
        </div>
      );
    }

    return (
      <div className="flex-fill gs-container">
        <Step1SelectEmployeeFilter
          onChange={this.handleFilterChange}
          onReset={this.handleResetFilter}
          onSubmit={this.handleFilterSubmit}
        />
        {!!this.props.employees?.length && (
          <GSGrid rows={this.props.employees} columns={this.columns} height={300} />
        )}
        {this.state.searchSubmitted && !this.props.employees?.length && !this.props.isLoading && (
          <View padding="0 1rem">{this.props.t("EMPLOYEES_EMPTY_SEARCH_MESSAGE")}</View>
        )}
        {this.props.pagination && (
          <Pagination
            className="mx-auto d-block"
            currentPage={this.props.pagination.pageNumber}
            totalPages={this.props.pagination.totalPages}
            onChange={this.handlePaginationChange}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => ({
  employees: getAddNotificationModalList(state),
  pagination: selectAddNotificationModalPagination(state),
  currentPath: getCurrentPath(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeeList: actionCreators.GetAddNotificationModalEmployeeList,
  resetEmployeeList: actionCreators.ResetAddNotificationModalEmployeeList,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(SelectEmployee);
